import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { fetchPaymentTermsOptions, savePaymentTermsOptions } from '../../services/paymentTermsService';

const PaymentTermsManager = () => {
  const [terms, setTerms] = useState([]);
  const [newTerm, setNewTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const list = await fetchPaymentTermsOptions();
      setTerms(list);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to load credit terms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = () => {
    const t = newTerm.trim();
    if (!t) return;
    if (terms.includes(t)) {
      setError('That term is already in the list');
      return;
    }
    setError('');
    setTerms((prev) => [...prev, t]);
    setNewTerm('');
  };

  const handleRemove = (index) => {
    setTerms((prev) => prev.filter((_, i) => i !== index));
    setSuccess('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const saved = await savePaymentTermsOptions(terms);
      setTerms(saved);
      setSuccess('Credit terms saved.');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to save credit terms');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading terms…
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        These options appear in customer credit settings and when editing invoice payment terms. Use labels the
        system understands, such as <strong>Net 20</strong> or <strong>Pay before ship</strong> (due dates use
        “Net” numbers when present).
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <List dense disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
        {terms.length === 0 ? (
          <ListItem>
            <ListItemText primary="No terms — add at least one below." />
          </ListItem>
        ) : (
          terms.map((term, index) => (
            <ListItem
              key={`${term}-${index}`}
              secondaryAction={
                <IconButton edge="end" aria-label={`Remove ${term}`} onClick={() => handleRemove(index)} size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              }
              sx={{ borderBottom: index < terms.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
            >
              <ListItemText primary={term} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))
        )}
      </List>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
        <TextField
          size="small"
          label="New term"
          value={newTerm}
          onChange={(e) => setNewTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="e.g. Net 20"
          sx={{ minWidth: 220, flex: '1 1 200px' }}
        />
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ mt: 0.5 }}>
          Add
        </Button>
      </Box>

      <Button variant="contained" size="small" disabled={saving || terms.length === 0} onClick={handleSave}>
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </Box>
  );
};

export default PaymentTermsManager;
